import { getApiClient } from '../axios-config';

/**
 * Autodesk Construction Cloud (ACC) Integration API Service
 * Handles all API calls related to ACC integration
 */
class ACCAPI {
  /**
   * Get authentication URL
   * @param {string} token - JWT authentication token
   * @returns {Promise<string>} Authentication URL
   */
  static async getAuthUrl(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/acc/auth/url');
    return response.data.data.authUrl;
  }

  /**
   * Get authentication status
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Authentication status object
   */
  static async getAuthStatus(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/acc/auth/status');
    return response.data.data;
  }

  /**
   * Start sync
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Sync result
   */
  static async sync(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/acc/sync');
    return response.data.data;
  }

  /**
   * Get sync status
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Sync status object
   */
  static async getSyncStatus(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/acc/sync/status');
    return response.data.data;
  }

  /**
   * Get sync schedule status
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Schedule status object
   */
  static async getSyncScheduleStatus(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/acc/sync/schedule/status');
    return response.data.data;
  }

  /**
   * Start sync schedule
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Schedule result
   */
  static async startSyncSchedule(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/acc/sync/schedule/start');
    return response.data.data;
  }

  /**
   * Stop sync schedule
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Schedule result
   */
  static async stopSyncSchedule(token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/acc/sync/schedule/stop');
    return response.data.data;
  }

  /**
   * Trigger ACC Data Connector schedule sync for a project
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Sync result with syncLogId, accRequestId, status
   */
  static async triggerScheduleSync(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(
      `/api/acc/schedule-sync/${projectId}`
    );
    return response.data.data;
  }

  /**
   * Get one project's ACC sync status (the same projects.acc_last_sync_*
   * values the Projects tab's "Last ACC Sync" column shows).
   *
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { projectId, lastSyncAt, lastSyncStatus,
   *   lastSyncError, isSyncRunning }
   */
  static async getProjectSyncStatus(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(`/api/acc/sync/status/${projectId}`);
    return response.data.data;
  }

  /**
   * Trigger an assets-only ACC sync for a single project.
   * Syncs status step sets, statuses, categories, custom attributes and
   * assets — the entities the Packages table depends on. RFIs and submittals
   * are not included; use the full project sync for those.
   *
   * Rejects with a 409 (error.reason === 'sync_already_running') when another
   * sync is already in progress anywhere in the app.
   *
   * @param {string} projectId - Project ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { projectId, scope, status, message }
   */
  static async syncProjectAssets(projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post(`/api/acc/sync/${projectId}/assets`);
    return response.data.data;
  }

  /**
   * Search ACC for the project matching a Fieldtime project number.
   *
   * The backend strips the trade letter and matches the remaining 7-digit job
   * number against the start of each ACC project's name, so a project number
   * that isn't 7 digits plus a letter is rejected with a 400 before any call to
   * ACC is made.
   *
   * @param {string} number - Full 8-character Fieldtime project number
   * @param {string|null} projectId - Project being linked, excluded from the
   *   already-linked lookup
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { jobNumber, matches: [{ autodeskProjectId,
   *   name, alreadyLinkedTo }] }
   */
  static async searchAccProjects(number, projectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get('/api/acc/projects/search', {
      params: { number, ...(projectId ? { projectId } : {}) },
    });
    return response.data.data;
  }

  /**
   * Link a Fieldtime project to an ACC project.
   *
   * Used by both the automatic-match confirm and the manual-entry fallback —
   * same endpoint, same validation, so a pasted ID gets the same rules as a
   * searched one. The backend normalizes the ID format, so callers can pass the
   * raw UUID from the ACC URL without any prefix handling.
   *
   * @param {string} projectId - Project ID
   * @param {string} autodeskProjectId - ACC project UUID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { project, previousAutodeskProjectId }
   */
  static async linkAccProject(projectId, autodeskProjectId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.post('/api/acc/projects/link', {
      projectId,
      autodeskProjectId,
    });
    return response.data.data;
  }

  /**
   * Get the linked schedule activity for a package
   * @param {string} packageId - Package ID
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} { linked, matchStatus, activity, link }
   */
  static async getPackageScheduleLink(packageId, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.get(`/api/packages/${packageId}/schedule`);
    return response.data.data;
  }

  /**
   * Update field_start_date with lock control and audit history
   * @param {string} packageId - Package ID
   * @param {Object} data - { fieldStartDate, locked, notes }
   * @param {string} token - JWT authentication token
   * @returns {Promise<Object>} Updated package data
   */
  static async updateFieldStartDate(packageId, data, token) {
    const apiClient = getApiClient(token);
    const response = await apiClient.patch(
      `/api/packages/${packageId}/field-start-date`,
      data
    );
    return response.data.data;
  }
}

export default ACCAPI;
